import { AfterRenderRef, Component, ElementRef, HostListener, Inject, Injector, OnDestroy, OnInit, ViewChild, afterNextRender, computed, effect, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { of } from 'rxjs';
import { filter, map, scan, startWith, switchMap } from 'rxjs/operators';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { OktaAuth } from '@okta/okta-auth-js';
import { HeaderComponent } from '@roosevelt/common-ui-lib/header';
import { BasicAuthConfig, CommonUserService, commonUtil, Lux } from '@roosevelt/common-ui-lib/core';
import { UserAuthConfig as EligUserAuth } from '@roosevelt/elig-ui-lib';
import { UserAuthConfig as ClientUserAuth } from '@roosevelt/client-ui-lib';
import { UserAuthConfig as BillingUserAuth } from '@roosevelt/billing-lib';
import { OneTrustService } from '@roosevelt/common-ui-lib/cookieConsentService';
import { AppConfig } from './shared/app.config';
import { MagnoliaService } from './magnolia/magnolia.service';
import { NotificationService } from './shared/services/notification.service';
import { GoogleAnalyticsService } from './shared/services/google-analytics.service';
import { PreviousRouteService } from './shared/services/previousUrl.service';
import { UpScopeService } from './shared/services/up-scope.service';
import { IAuthInfo, ILoginState } from './login/store/state';
import { getImpersonationUser, getUserAuths, getUserInfo } from './login/store/selectors';
import { IAlertsState, IGeneralAlerts } from './store/alerts/state';
import { alertErrors, getBMTAlerts } from './store/alerts/selector';
import { IAppAuthorizations } from './authorizations/state';
import { ICreateManageUserState } from './store/createManageUser/state';
import { isFetchingContext, isFetchingUserLists } from './store/createManageUser/selector';

declare const dT_: any;

type InactivityTimeoutState = ReturnType<typeof HeaderComponent.setUpTimeout>;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnDestroy, OnInit {
  private readonly injector = inject(Injector);
  private inactivitySetupScheduled = false;
  private inactivitySetupRef: AfterRenderRef | null = null;
  private magnoliaHeaderRenderRef: AfterRenderRef | null = null;
  private magnoliaFooterRenderRef: AfterRenderRef | null = null;
  private readonly emptyInactivityDisplay = new Date(0, 0, 0, 0, 0, 0, 0);

  readonly luxId = 'BMT-AppComponent' + new Date().toISOString();
  readonly title = 'bmt-ui';
  readonly region = this.config.region;
  readonly userLoginInProgress = false;
  readonly notificationService: NotificationService;

  readonly activeUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects || event.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );
  readonly userInfo = toSignal(this.lux.get(getUserInfo, this.luxId), { initialValue: null as IAuthInfo | null });
  readonly userAuths = toSignal(this.lux.get(getUserAuths, this.luxId), { initialValue: {} as { [key: string]: string[] } });
  readonly bmtAlerts = toSignal(this.lux.get(getBMTAlerts, this.luxId), { initialValue: [] as IGeneralAlerts[] });
  readonly bmtAlertErrors = toSignal(this.lux.get(alertErrors, this.luxId), { initialValue: '' });
  readonly fetchingContext = toSignal(this.lux.get(isFetchingContext, this.luxId), { initialValue: false });
  readonly fetchingUserLists = toSignal(this.lux.get(isFetchingUserLists, this.luxId), { initialValue: false });
  readonly impersonationUser = toSignal(this.lux.get(getImpersonationUser, this.luxId), { initialValue: '' });
  readonly headerFooterContent = toSignal(this.magnoliaService.getMagnoliaContent('headerFooter'), { initialValue: '' });

  readonly showBmtAlerts = signal(false);
  readonly impersonationSessionActive = signal(this.hasImpersonationSession());
  private readonly inactivityTimeoutState = signal<InactivityTimeoutState | null>(null);
  private readonly inactivityTimeoutState$ = toObservable(this.inactivityTimeoutState, { injector: this.injector });
  private readonly inactivityLogoutEvents = toSignal(
    this.inactivityTimeoutState$.pipe(
      switchMap(timeoutState => timeoutState
        ? timeoutState.shouldLogOut$.pipe(scan(logoutCount => logoutCount + 1, 0))
        : of(0))
    ),
    { initialValue: 0 }
  );
  readonly countdown = toSignal(
    this.inactivityTimeoutState$.pipe(
      switchMap(timeoutState => timeoutState?.countdown$ ?? of(0))
    ),
    { initialValue: 0 }
  );
  readonly countdownTotalInSeconds = computed(() => this.inactivityTimeoutState()?.countdownTotalInSeconds ?? 0);
  readonly inactivityDisplay = computed(() => this.inactivityTimeoutState()?.inactivityDisplay ?? this.emptyInactivityDisplay);
  readonly inactivityDialogVisible = computed(() => this.countdown() > 0);
  readonly refreshUrl = signal<SafeResourceUrl | undefined>(undefined);
  readonly upScopeCode = signal('');
  readonly upScopeAllowed = signal(false);
  readonly showUpScopeError = signal(false);
  readonly usersId = computed(() => this.userInfo()?.id || '');
  readonly isImpersonating = computed(() => this.impersonationSessionActive() || !!this.impersonationUser());
  readonly showUpScopeShare = computed(() => this.upScopeAllowed());

  readonly isInactivityTrackingRoute = computed(() => this.shouldTrackInactivity(this.activeUrl()));
  readonly registrationPage = computed(() => this.activeUrl().includes('registration'));
  readonly urlNotDefined = computed(() => this.isUnknownRoute(this.activeUrl()));
  readonly showShellChrome = computed(() => {
    const url = this.activeUrl();
    return !this.urlNotDefined() && url !== '/login' && url !== '/' && !this.registrationPage() && url !== '/404';
  });
  readonly showFooter = computed(() => {
    const url = this.activeUrl();
    return !this.urlNotDefined() && url !== '/404' && url !== '/';
  });
  readonly showAlertsLauncher = computed(() => {
    const url = this.activeUrl();
    return !this.urlNotDefined() && url !== '/login' && url !== '/' && !this.registrationPage();
  });
  readonly showLoader = computed(() => !!this.fetchingContext() || this.userLoginInProgress || !!this.fetchingUserLists());
  readonly magnoliaFooterMarkup = computed(() => {
    const content = this.headerFooterContent();
    if (!content) {
      return '';
    }

    return this.extractSection(content, 'footerContent');
  });
  readonly magnoliaHeaderMarkup = computed(() => {
    const content = this.headerFooterContent();
    if (!content || !this.userInfo()?.id) {
      return '';
    }
    const personalizedContent = content.replace('profileName', this.commonUserService?.getFullName() || '');
    return this.extractSection(personalizedContent, 'headerContent');
  });

  readonly postLogoutRedirectUrl: string;
  readonly trustGuardUrl: string;
  readonly themeStyleHrefs: string[];
  readonly dataDomainId: string;
  readonly isRenTheme: boolean;
  @ViewChild('magnoliaHeaderHost')
  private readonly magnoliaHeaderHost?: ElementRef<HTMLElement>;
  @ViewChild('magnoliaFooterHost')
  private readonly magnoliaFooterHost?: ElementRef<HTMLElement>;
  private inactivityTimeoutCleanup: (() => void) | null = null;
  private readonly themeStyleElements: HTMLLinkElement[] = [];

  constructor(
    @Inject(DOCUMENT) private readonly docRef: Document,
    private readonly env: AppConfig,
    routerService: PreviousRouteService,
    private readonly router: Router,
    private readonly analyticsService: GoogleAnalyticsService,
    private readonly lux: Lux<{ login: ILoginState, bmtAlertsState: IAlertsState, appUiAuth: IAppAuthorizations, createManageUserState: ICreateManageUserState }>,
    private readonly config: AppConfig,
    private readonly magnoliaService: MagnoliaService,
    @Inject(BasicAuthConfig) private readonly basicAuthConfig: BasicAuthConfig,
    @Inject(EligUserAuth) private readonly eligUserAuth: EligUserAuth,
    @Inject(ClientUserAuth) private readonly clientUserAuth: ClientUserAuth,
    @Inject(BillingUserAuth) private readonly billingUserAuth: BillingUserAuth,
    private readonly http: HttpClient,
    private readonly oktaAuth: OktaAuth,
    private readonly commonUserService: CommonUserService,
    notificationService: NotificationService,
    private readonly oneTrustService: OneTrustService,
    private readonly upScope: UpScopeService,
  ) {
    this.notificationService = notificationService;

    const pathSegment = this.getPathSegment();
    this.isRenTheme = pathSegment === 'ren';
    if (pathSegment === 'ren') {
      this.themeStyleHrefs = ['assets/rlha.css', './assets/css/base.css', './assets/css/renBase.css'];
      this.dataDomainId = this.config.oneTrustDataDomainId['ren'];
    } else {
      this.themeStyleHrefs = ['assets/delta.css', './assets/css/base.css'];
      this.dataDomainId = this.config.oneTrustDataDomainId['delta'];
    }
    this.attachThemeStyles();

    this.trustGuardUrl = this.env.trustGuardSecuritySeals[pathSegment];
    this.postLogoutRedirectUrl = `${this.env.postLogoutRedirectEndpoint}${pathSegment}/`;

    if (typeof dT_ !== 'undefined' && dT_.initAngularNg) {
      dT_.initAngularNg(this.http, HttpHeaders);
    }

    routerService.loadRouting();

    effect(() => {
      const adminPlansAndPlans = this.userAuths();
      this.eligUserAuth.userAuthorizations = adminPlansAndPlans || {};
      this.clientUserAuth.userAuthorizations = adminPlansAndPlans || {};
      this.billingUserAuth.userAuthorizations = adminPlansAndPlans || {};
    });

    effect(() => {
      const shouldTrackInactivity = this.isInactivityTrackingRoute();
      if (shouldTrackInactivity) {
        if (!this.inactivityTimeoutState() && !this.inactivitySetupScheduled) {
          this.scheduleInactivitySetup();
        }
        return;
      }

      this.clearScheduledInactivitySetup();
      this.clearInactivityTimeouts();
    });

    effect(() => {
      if (this.inactivityLogoutEvents() > 0) {
        // zoneless/signals-first: treat the shared header stream as a logout event source.
        // The library currently emits 0 on expiry, so we react to the emission count instead.
        this.logOut();
      }
    });

    effect(() => {
      const showHeader = this.showShellChrome();
      const headerMarkup = this.magnoliaHeaderMarkup();
      if (!showHeader || !headerMarkup) {
        this.clearScheduledMagnoliaHeaderRender();
        this.clearMagnoliaHeaderHost();
        return;
      }

      this.scheduleMagnoliaHeaderRender(headerMarkup);
    });

    effect(() => {
      const showFooter = this.showFooter();
      const footerMarkup = this.magnoliaFooterMarkup();
      if (!showFooter || !footerMarkup) {
        this.clearScheduledMagnoliaFooterRender();
        this.clearMagnoliaFooterHost();
        return;
      }

      this.scheduleMagnoliaFooterRender(footerMarkup);
    });
  }

  ngOnInit(): void {
    if (this.dataDomainId) {
      this.oneTrustService.injectOneTrustScript(this.dataDomainId, [
        () => this.analyticsService.googleAnalyticsG4(),
        () => {
          this.upScope.upScopeCookie();
          this.syncUpScopeSharingState();
        }
      ]);
    }

    this.syncUpScopeSharingState();
    const initialLoader = this.docRef.querySelector<HTMLElement>('#initail-loader-container');
    if (initialLoader) {
      initialLoader.style.display = 'none';
    }

    this.analyticsService.googleAnalyticsG4();

  }

  ngOnDestroy(): void {
    this.clearScheduledMagnoliaHeaderRender();
    this.clearScheduledMagnoliaFooterRender();
    this.clearScheduledInactivitySetup();
    this.clearInactivityTimeouts();
    this.detachThemeStyles();
    this.lux.destroy(this.luxId);
  }

  closeThisTab(): void {
    window.close();
  }

  viewAlerts(): void {
    this.showBmtAlerts.set(true);
  }

  onInactivityDialogVisibleChange(visible: boolean): void {
    if (visible || !this.inactivityDialogVisible()) {
      return;
    }

    // rs-header inactivity utility resets only on document clicks.
    // Triggering a synthetic click keeps the dialog close action consistent with user activity.
    localStorage.setItem('last-activity-at', Date.now().toString());
    this.docRef.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  headerClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.classList.contains('magnolia-logout-btn')) {
      this.logOut();
    }
  }

  @HostListener('window:storage', ['$event'])
  manageAuthState(event: StorageEvent): void {
    if (event.key === 'logout-catalyst' && event.newValue) {
      this.logOut();
      return;
    }

    if (event.key === 'impersonate_user') {
      this.impersonationSessionActive.set(this.hasImpersonationSession());
    }
  }

  logOut(): void {
    this.clearScheduledInactivitySetup();
    this.clearInactivityTimeouts();
    localStorage.setItem('logout-catalyst', this.config.appName);
    localStorage.setItem('logout-catalyst', '');
    sessionStorage.clear();
    localStorage.clear();
    this.impersonationSessionActive.set(false);
    this.oktaAuth.options.postLogoutRedirectUri = this.postLogoutRedirectUrl;
    commonUtil.authentication.signOut(this.oktaAuth);
  }

  setUpInactiveAndRefresh(): void {
    const inactivityTimeout = 30 * 1000 * 60;
    const timeoutState = HeaderComponent.setUpTimeout(inactivityTimeout);
    this.setInactivityTimeoutCleanup(() => {
      // zoneless: HeaderComponent returns library-owned timeout subscriptions that still need
      // explicit teardown until that utility exposes a DestroyRef-friendly cleanup API.
      timeoutState.subscriptions.forEach(subscription => subscription.unsubscribe());
    });
    this.inactivityTimeoutState.set(timeoutState);
  }

  private setInactivityTimeoutCleanup(cleanup: () => void): void {
    this.clearInactivityTimeouts();
    this.inactivityTimeoutCleanup = cleanup;
  }

  private scheduleInactivitySetup(): void {
    this.clearScheduledInactivitySetup();
    this.inactivitySetupScheduled = true;
    this.inactivitySetupRef = afterNextRender(() => {
      this.inactivitySetupRef = null;
      this.inactivitySetupScheduled = false;

      if (this.inactivityTimeoutState() || !this.isInactivityTrackingRoute()) {
        return;
      }

      this.setUpInactiveAndRefresh();
    }, { injector: this.injector });
  }

  private scheduleMagnoliaHeaderRender(content: string): void {
    this.clearScheduledMagnoliaHeaderRender();
    this.magnoliaHeaderRenderRef = afterNextRender(() => {
      this.magnoliaHeaderRenderRef = null;

      const host = this.magnoliaHeaderHost?.nativeElement;
      if (!host || !this.showShellChrome()) {
        return;
      }

      host.replaceChildren(this.createSanitizedMagnoliaFragment(content));
    }, { injector: this.injector });
  }

  private scheduleMagnoliaFooterRender(content: string): void {
    this.clearScheduledMagnoliaFooterRender();
    this.magnoliaFooterRenderRef = afterNextRender(() => {
      this.magnoliaFooterRenderRef = null;

      const host = this.magnoliaFooterHost?.nativeElement;
      if (!host || !this.showFooter()) {
        return;
      }

      host.replaceChildren(this.createSanitizedMagnoliaFragment(content));
    }, { injector: this.injector });
  }

  private clearScheduledInactivitySetup(): void {
    this.inactivitySetupRef?.destroy();
    this.inactivitySetupRef = null;
    this.inactivitySetupScheduled = false;
  }

  private clearScheduledMagnoliaHeaderRender(): void {
    this.magnoliaHeaderRenderRef?.destroy();
    this.magnoliaHeaderRenderRef = null;
  }

  private clearScheduledMagnoliaFooterRender(): void {
    this.magnoliaFooterRenderRef?.destroy();
    this.magnoliaFooterRenderRef = null;
  }

  private clearMagnoliaHeaderHost(): void {
    this.magnoliaHeaderHost?.nativeElement.replaceChildren();
  }

  private clearMagnoliaFooterHost(): void {
    this.magnoliaFooterHost?.nativeElement.replaceChildren();
  }

  private clearInactivityTimeouts(): void {
    this.inactivityTimeoutCleanup?.();
    this.inactivityTimeoutCleanup = null;
    if (this.inactivityTimeoutState()) {
      this.inactivityTimeoutState.set(null);
    }
  }

  private syncUpScopeSharingState(): void {
    const isUpscopeAllowed = this.oneTrustService.isUpscopeAllowed;
    this.upScopeAllowed.set(isUpscopeAllowed);
    if (!isUpscopeAllowed) {
      this.upScopeCode.set('');
      this.showUpScopeError.set(false);
    }
  }

  private hasImpersonationSession(): boolean {
    return !!sessionStorage.getItem('impersonate_user');
  }

  private getPathSegment(): string {
    return this.docRef.location.pathname.split('/')[1].toLowerCase();
  }

  onUpScopeLookupCodeClick(evt: Event): void {
    evt.preventDefault();
    this.syncUpScopeSharingState();
    if (!this.upScopeAllowed()) {
      this.showUpScopeError.set(true);
      return;
    }

    try {
      (window as any).Upscope('getLookupCode', (code: string) => {
        this.upScopeCode.set(`Please tell our agent code: ${code}`);
      });
    } catch (e) {
      console.error('Upscope not ready yet', e);
    }
  }

  openTrustGuardPopup(event: Event): void {
    event.preventDefault();
    const userAgent = this.docRef.defaultView?.navigator.userAgent || '';
    const isInternetExplorer = userAgent.includes('MSIE') || userAgent.includes('Trident/');
    const nonInternetExplorerLocation = isInternetExplorer ? 'no' : 'yes';
    const popup = this.docRef.defaultView?.open(
      `https://secure.trust-guard.com/security/${this.trustGuardUrl}`,
      'welcome',
      `location=${nonInternetExplorerLocation},scrollbars=yes,width=517,height=${screen.availHeight},menubar=no,toolbar=no,noopener,noreferrer`
    );
    if (popup) {
      popup.opener = null;
    }
  }

  blockTrustGuardCopy(event: Event): void {
    event.preventDefault();
    const currentYear = new Date().getFullYear();
    this.docRef.defaultView?.alert(`Copying Prohibited by Law - This image and all included logos are copyrighted by trust-guard © ${currentYear}.`);
  }

  private attachThemeStyles(): void {
    this.detachThemeStyles();
    const head = this.docRef.head;
    if (!head) {
      return;
    }

    this.themeStyleElements.push(...this.themeStyleHrefs.map(href => {
      const link = this.docRef.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      head.appendChild(link);
      return link;
    }));
  }

  private detachThemeStyles(): void {
    this.themeStyleElements.splice(0).forEach(link => link.remove());
  }

  private extractSection(content: string, tagName: 'headerContent' | 'footerContent'): string {
    return content.substring(
      content.lastIndexOf(`<${tagName}>`) + tagName.length + 2,
      content.lastIndexOf(`</${tagName}>`)
    );
  }


  private createSanitizedMagnoliaFragment(content: string): DocumentFragment {
    const fragment = this.docRef.createDocumentFragment();
    const parsedDocument = new DOMParser().parseFromString(content, 'text/html');

    Array.from(parsedDocument.body.childNodes).forEach(node => {
      const sanitizedNode = this.sanitizeMagnoliaNode(node);
      if (sanitizedNode) {
        fragment.appendChild(sanitizedNode);
      }
    });

    return fragment;
  }

  private sanitizeMagnoliaNode(node: Node): Node | null {
    if (node.nodeType === Node.TEXT_NODE) {
      return this.docRef.createTextNode(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const sourceElement = node as Element;
    const tagName = sourceElement.tagName.toLowerCase();
    if (this.isBlockedMagnoliaTag(tagName)) {
      return null;
    }

    const sanitizedElement = this.createMagnoliaElementClone(sourceElement);
    this.copySafeMagnoliaAttributes(sourceElement, sanitizedElement);

    Array.from(sourceElement.childNodes).forEach(childNode => {
      const sanitizedChildNode = this.sanitizeMagnoliaNode(childNode);
      if (sanitizedChildNode) {
        sanitizedElement.appendChild(sanitizedChildNode);
      }
    });

    return sanitizedElement;
  }

  private createMagnoliaElementClone(sourceElement: Element): Element {
    const namespace = sourceElement.namespaceURI;
    return namespace && namespace !== 'http://www.w3.org/1999/xhtml'
      ? this.docRef.createElementNS(namespace, sourceElement.tagName)
      : this.docRef.createElement(sourceElement.tagName.toLowerCase());
  }

  private copySafeMagnoliaAttributes(sourceElement: Element, targetElement: Element): void {
    Array.from(sourceElement.attributes).forEach(attribute => {
      const attributeName = attribute.name.toLowerCase();
      if (attributeName.startsWith('on')) {
        return;
      }

      if (attributeName === 'style' && this.containsUnsafeMagnoliaStyle(attribute.value)) {
        return;
      }

      if (this.isMagnoliaUrlAttribute(attributeName) && !this.isSafeMagnoliaUrl(attribute.value)) {
        return;
      }

      if (attribute.namespaceURI) {
        targetElement.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
        return;
      }

      targetElement.setAttribute(attribute.name, attribute.value);
    });
  }

  private containsUnsafeMagnoliaStyle(styleValue: string): boolean {
    const normalizedStyle = styleValue.toLowerCase();
    return normalizedStyle.includes('expression(')
      || normalizedStyle.includes('behavior:')
      || normalizedStyle.includes('-moz-binding')
      || this.containsUnsafeMagnoliaStyleUrl(normalizedStyle);
  }

  private containsUnsafeMagnoliaStyleUrl(styleValue: string): boolean {
    let searchStart = 0;
    while (searchStart < styleValue.length) {
      const urlIndex = styleValue.indexOf('url(', searchStart);
      if (urlIndex < 0) {
        return false;
      }

      const closeIndex = styleValue.indexOf(')', urlIndex + 4);
      if (closeIndex < 0) {
        return true;
      }

      const rawUrl = this.stripWrappingQuotes(styleValue.slice(urlIndex + 4, closeIndex).trim());
      if (!this.isSafeMagnoliaUrl(rawUrl)) {
        return true;
      }

      searchStart = closeIndex + 1;
    }

    return false;
  }

  private isMagnoliaUrlAttribute(attributeName: string): boolean {
    return attributeName === 'href'
      || attributeName === 'src'
      || attributeName === 'xlink:href'
      || attributeName === 'action'
      || attributeName === 'formaction'
      || attributeName === 'poster';
  }

  private isSafeMagnoliaUrl(url: string): boolean {
    const trimmedUrl = (url || '').trim();
    if (!trimmedUrl) {
      return false;
    }

    if (trimmedUrl.startsWith('#')
      || trimmedUrl.startsWith('/')
      || trimmedUrl.startsWith('./')
      || trimmedUrl.startsWith('../')
      || trimmedUrl.startsWith('?')) {
      return true;
    }

    try {
      const resolvedUrl = new URL(trimmedUrl, this.docRef.baseURI || 'https://bmt.local');
      return resolvedUrl.protocol === 'http:' || resolvedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private isBlockedMagnoliaTag(tagName: string): boolean {
    return tagName === 'script'
      || tagName === 'iframe'
      || tagName === 'object'
      || tagName === 'embed'
      || tagName === 'applet'
      || tagName === 'base'
      || tagName === 'meta';
  }

  private stripWrappingQuotes(value: string): string {
    if (!value) {
      return '';
    }

    const hasMatchingSingleQuotes = value.startsWith('\'') && value.endsWith('\'');
    const hasMatchingDoubleQuotes = value.startsWith('"') && value.endsWith('"');
    return hasMatchingSingleQuotes || hasMatchingDoubleQuotes
      ? value.slice(1, -1)
      : value;
  }

  private isUnknownRoute(url: string): boolean {
    const normalizedUrl = (url || '').split('?')[0];
    const knownRoutePrefixes = [
      '/login',
      '/logout',
      '/oidc-callback',
      '/eligibility/',
      '/details',
      '/benefits',
      '/reports',
      '/dentist',
      '/add-member',
      '/summary-of-changes',
      '/sat',
      '/registration',
      '/billing',
      '/userAdmin/',
      '/manage-user',
      '/notAuthorized',
      '/profile/',
      '/clientknowledge',
      '/help',
      '/family-enrollment'
    ];

    return normalizedUrl !== '/'
      && !knownRoutePrefixes.some(routePrefix => normalizedUrl === routePrefix || normalizedUrl.startsWith(routePrefix));
  }

  private shouldTrackInactivity(url: string): boolean {
    const normalizedUrl = (url || '').split('?')[0];
    return normalizedUrl !== '/'
      && normalizedUrl !== '/login'
      && normalizedUrl !== '/logout'
      && normalizedUrl !== '/oidc-callback'
      && !normalizedUrl.startsWith('/registration')
      && !this.isUnknownRoute(normalizedUrl);
  }
}
